// src/MainEngineSubsection.jsx

import React, { useState, useEffect, useRef } from 'react';

export default function MainEngineSubsection({
  initialData = { numberOfMainEngines: 0, engines: [] },
  onDataChange,
  isEditMode = false,
}) {
  // Destructure for clarity
  const { numberOfMainEngines: initCount, engines: initEngines } = initialData;

  // Local state mirrors the props
  const [count, setCount] = useState(initCount);
  const [engines, setEngines] = useState(initEngines);

  // Ref to skip exactly one notification after syncing props→state
  const skipNextNotify = useRef(false);

  // 1️⃣ Sync incoming props into local state *only when they truly differ*
  useEffect(() => {
    let didSync = false;

    if (count !== initCount) {
      setCount(initCount);
      didSync = true;
    }

    // simple deep-equality check for array contents
    if (JSON.stringify(engines) !== JSON.stringify(initEngines)) {
      setEngines(initEngines);
      didSync = true;
    }

    // if we updated either, skip the very next onDataChange
    skipNextNotify.current = didSync;
  }, [initCount, initEngines]); // ← only re-run when the parent actually passes different data

  // 2️⃣ Notify parent when local state changes (but skip the first "sync" notification)
  useEffect(() => {
    if (skipNextNotify.current) {
      skipNextNotify.current = false;
      return;
    }
    onDataChange({ numberOfMainEngines: count, engines });
  }, [count, engines, onDataChange]);

  // Handlers
  const handleCountChange = (e) => {
    const n = parseInt(e.target.value, 10) || 0;
    setCount(n);
    setEngines(prev =>
      Array.from({ length: n }, (_, i) => prev[i] || {})
    );
  };

  const handleFieldChange = (idx, field, value) => {
    setEngines(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  return (
    <div className="mb-4">
      <h5 className="fw-bold">Main Engines</h5>

      {!isEditMode && (
        <div className="row mb-3">
          <div className="col-md-3">
            <label className="form-label">Number of Main Engines</label>
            <select
              className="form-select"
              value={count}
              onChange={handleCountChange}
            >
              <option value={0}>Select…</option>
              <option value={1}>1</option>
              <option value={2}>2</option>
            </select>
          </div>
        </div>
      )}

      {Array.from(
        { length: isEditMode ? engines.length : count },
        (_, i) => i
      ).map(idx => (
        <div key={idx} className="border p-3 mb-3">
          <h6>Engine {idx + 1}</h6>
          <div className="row g-2">
            {[
              ['make', 'Make', 'text'],
              ['type', 'Type', 'text'],
              ['bore', 'Bore', 'number'],
              ['stroke', 'Stroke', 'number'],
              ['mcr', 'MCR', 'number'],
              ['cylinders', 'Cylinders', 'number'],
              ['turbochargers', 'Turbochargers', 'number'],
            ].map(([field, label, type]) => (
              <div className="col-md-3" key={field}>
                <label className="form-label">{label}</label>
                <input
                  type={type}
                  className="form-control"
                  value={engines[idx]?.[field] ?? ''}
                  onChange={e =>
                    handleFieldChange(idx, field, e.target.value)
                  }
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
